import React, { ForwardedRef, ReactElement, ReactNode, Ref, forwardRef, useImperativeHandle } from 'react';

import { DataTable, type DataTableProps } from '@column/ui';

import { Loading } from '~/components/Loading';
import type { PopoverFilterEntry } from '~/components/Popover';
import { type UseCursorPaginationOptions, useCursorPagination } from '~/lib/api/hooks/useCursorPagination';

import { tableDefaults } from './defaults';
import { Empty, type EmptyProps } from './Empty';
import { Pagination } from './Pagination';
import { Toolbar } from './Toolbar';

export interface PaginatedDataTableRef {
  refetch: () => void;
}

export interface PaginatedDataTableProps<T extends Record<string, any>> extends Omit<
  DataTableProps<T>,
  'data' | 'isLoading' | 'className' | 'ref' | 'emptyContent'
> {
  tableId: string;
  fetch: UseCursorPaginationOptions<T>['fetch'];
  fetchSearch?: UseCursorPaginationOptions<T>['fetchSearch'];
  fetchId?: string;
  filter?: PopoverFilterEntry[];
  useQueryParams?: boolean;
  onError?: UseCursorPaginationOptions<T>['onError'];
  empty?: EmptyProps;
  isFiltered?: boolean;
  filterElement?: ReactNode;
  action?: ReactNode;
  searchTooltip?: ReactNode;
  children?: ReactNode;
  className?: string;
}

const PaginatedDataTableInner = <T extends Record<string, any>>(
  {
    tableId,
    fetch,
    fetchSearch,
    fetchId = 'id',
    filter,
    useQueryParams = true,
    onError,
    empty = { title: 'No entries found' },
    isFiltered: isFilteredProp,
    filterElement,
    action,
    searchTooltip,
    children,
    className,
    rowKey,
    ...dataTableProps
  }: PaginatedDataTableProps<T>,
  ref: ForwardedRef<PaginatedDataTableRef>
) => {
  const table = useCursorPagination<T>({
    tableId,
    fetch,
    fetchSearch,
    fetchId,
    filter,
    syncQueryParams: useQueryParams,
    onError,
  });

  useImperativeHandle(ref, () => ({ refetch: table.refetch }), [table.refetch]);

  const isFiltered = isFilteredProp || table.isFiltered;

  return (
    <Loading isLoading={table.isLoading && !table.hasLoaded}>
      <div className={className}>
        {children}
        <Toolbar
          filterElement={filterElement}
          filters={table.filters}
          onFiltersChange={table.setFilters}
          onSearchSubmit={table.onSearchSubmit}
          searchValue={table.searchTerm}
          searchTooltip={searchTooltip}
          searchShowReset={table.searchTerm.length > 0}
          searchLoading={table.searchLoading}
          action={action}
        />
        <div className="px-[var(--page-margin)]">
          <DataTable<T>
            {...tableDefaults}
            {...dataTableProps}
            data={table.list}
            isLoading={table.isLoading}
            rowKey={rowKey ?? ((row, index) => row[fetchId] ?? index)}
            emptyContent={
              <Empty
                {...empty}
                description={isFiltered ? 'No entries were found matching the specified filters' : empty.description}
                action={isFiltered ? undefined : empty.action}
              />
            }
          />
        </div>
        <Pagination {...table.pagination} className="px-[var(--page-margin)] pb-6" />
      </div>
    </Loading>
  );
};

export const PaginatedDataTable = forwardRef(PaginatedDataTableInner) as <T extends Record<string, any>>(
  props: PaginatedDataTableProps<T> & { ref?: Ref<PaginatedDataTableRef> }
) => ReactElement;
